/*
 * Generic PaginatedProcessor for processing paginated resources in parallel.
 */
import { ParallelBatchProcessor } from './ParallelBatchProcessor.ts';

export interface PaginatedProcessorOptions<TPageArg, TResult> {
  pageSize: number;
  maxParallelPages: number;
  getTotal: () => Promise<number>;
  processPage: (pageArg: TPageArg, pageIndex: number) => Promise<TResult>;
}

/**
 * Orchestrates paginated, parallel processing of any resource.
 *
 * TPageArg: The argument type for a single page (e.g., page index, custom params)
 * TResult: The result type returned by processing a single page
 */
export class PaginatedProcessor<TPageArg, TResult> {
  pageSize: number;

  parallelBatcher: ParallelBatchProcessor<TPageArg>;

  getTotal: () => Promise<number>;

  processPage: (pageArg: TPageArg, pageIndex: number) => Promise<TResult>;

  constructor(options: PaginatedProcessorOptions<TPageArg, TResult>) {
    this.pageSize = options.pageSize;
    this.getTotal = options.getTotal;
    this.processPage = options.processPage;
    this.parallelBatcher = new ParallelBatchProcessor<TPageArg>(options.maxParallelPages);
  }

  /**
   * Processes all pages in parallel (up to maxParallelPages).
   * Returns an array of results from each page's processing.
   *
   * @param pageArgFactory - Function to generate the argument for each page, given (pageIndex, pageSize)
   */
  async processAllPages(
    pageArgFactory: (pageIndex: number, pageSize: number) => TPageArg,
  ): Promise<TResult[]> {
    const total = await this.getTotal();
    const totalPages = Math.ceil(total / this.pageSize);
    const pageArgs = Array.from({ length: totalPages }, (_, i) => pageArgFactory(i, this.pageSize));
    const batches = pageArgs.map(arg => [arg]);

    const results: TResult[] = [];
    await this.parallelBatcher.processBatches(batches, async batch => {
      const pageArg = batch[0];
      const pageIndex = pageArgs.indexOf(pageArg);
      const res = await this.processPage(pageArg, pageIndex);
      results.push(res);
    });

    return results;
  }
}
